import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStoreActions } from "app/utils/hooks";
import { fetchLists } from "app/actions/list";
import { ListModel } from "app/models";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

interface IProps {
  selectedListId?: string;
  onSelect?: (_listId: string) => void;
}

const AddToListConfig = ({ selectedListId, onSelect }: IProps) => {
  const { t } = useTranslation();
  const { list } = useStoreActions({ list: fetchLists });
  const [isOpen, setIsOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<ListModel | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  const allLists: ListModel[] = list?.data || [];

  // Pre-select list from workflow data
  useEffect(() => {
    if (selectedListId && allLists.length > 0 && !selectedList) {
      const match = allLists.find((l) => l.id === selectedListId);
      if (match) {
        setSelectedList(match);
      }
    }
  }, [selectedListId, allLists]);

  const handleSelectItem = (listItem: ListModel) => {
    setSelectedList(listItem);
    setIsOpen(false);
    onSelect?.(listItem.id);
  };

  const handleRemove = () => {
    setSelectedList(null);
    onSelect?.(null as any);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (
      selectRef.current &&
      !selectRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false);
    }
  };

  const getAvatars = (listItem: ListModel) =>
    listItem.spaces?.map((space) => space.avatar).slice(-3) || [];

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {t(
          "Select the lists where the Spaces will be added after they are created.",
        )}
      </p>

      <span className={styles.label}>{t("List")}</span>

      <div
        className={styles.select_wrapper}
        ref={selectRef}
        onBlur={handleBlur}
      >
        <button
          className={cls(styles.select_btn, { [styles.open]: isOpen })}
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <span
            className={cls({
              [styles.placeholder]: !selectedList,
            })}
          >
            {selectedList ? selectedList.name : t("Select")}
          </span>
          <i
            className={cls("ico", "ico-chevron", styles.chevron, {
              [styles.chevron_open]: isOpen,
            })}
          />
        </button>

        {isOpen && (
          <div className={styles.options_list}>
            {list?.pending && (
              <div className={styles.option_loading}>{t("Loading...")}</div>
            )}
            {allLists.map((listItem) => (
              <div
                key={listItem.id}
                className={cls(styles.option_item, {
                  [styles.option_selected]: selectedList?.id === listItem.id,
                })}
                onMouseDown={() => handleSelectItem(listItem)}
              >
                <span>{listItem.name}</span>
                {selectedList?.id === listItem.id && (
                  <i className={cls("ico", "ico-check", styles.check_icon)} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedList && (() => {
        const avatars = getAvatars(selectedList);
        return (
          <div className={styles.list_cards}>
            <div className={styles.list_card}>
              <div className={styles.avatar_cluster}>
                {avatars.length > 0 ? (
                  avatars.map((avatar, idx) => (
                    <div key={idx} className={styles.avatar_shape}>
                      {avatar ? (
                        <img src={avatar} alt="" />
                      ) : (
                        <i className="ico ico-groups" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className={styles.avatar_shape}>
                    <i className="ico ico-groups" />
                  </div>
                )}
              </div>
              <div className={styles.list_info}>
                <span className={styles.list_name}>{selectedList.name}</span>
                <span className={styles.list_count}>
                  {t("{{count}} Spaces", {
                    count: selectedList.spaceIds?.length || 0,
                  })}
                </span>
              </div>
              <button
                className={styles.remove_btn}
                onClick={handleRemove}
                type="button"
              >
                &times;
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AddToListConfig;
